"use client";

import { useState, useRef, useCallback } from "react";
import api, { type LocaleCode } from "./api/client";

interface UseSpeechToTextOptions {
  language?: LocaleCode;
  onResult: (transcript: string) => void;
  onInterim?: (transcript: string) => void;
}

/**
 * Hook for speech-to-text — always records audio via MediaRecorder and sends
 * to /api/speech-to-text. The backend decides which provider to use (Azure or
 * OpenAI Whisper).
 */
export function useSpeechToText({
  language = "en-US",
  onResult,
}: UseSpeechToTextOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
  // Simple volume level (0-1)
  const [volumeLevel, setVolumeLevel] = useState(0);
  const smoothedVolumeRef = useRef(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  // Start audio visualization - simple volume detection
  const startAudioVisualization = useCallback(async (stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.fftSize);

      const updateVolume = () => {
        if (!analyserRef.current) return;

        // Get time domain data (waveform) for volume
        analyserRef.current.getByteTimeDomainData(dataArray);

        // Calculate RMS (root mean square) for volume level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const value = (dataArray[i]! - 128) / 128; // Normalize to -1 to 1
          sum += value * value;
        }
        const rms = Math.sqrt(sum / dataArray.length);

        // Scale and clamp (rms is typically 0-0.5 for speech)
        const rawVolume = Math.min(1, rms * 3);

        // Smooth the volume with fast attack, slow decay
        const prev = smoothedVolumeRef.current;
        const smoothed = rawVolume > prev
          ? prev + (rawVolume - prev) * 0.5  // Fast rise
          : prev * 0.92;  // Slow decay

        smoothedVolumeRef.current = smoothed;
        setVolumeLevel(smoothed);

        animationRef.current = requestAnimationFrame(updateVolume);
      };
      updateVolume();
    } catch (err) {
      console.error("Failed to start audio visualization:", err);
    }
  }, []);

  // Stop audio visualization
  const stopAudioVisualization = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    smoothedVolumeRef.current = 0;
    setVolumeLevel(0);
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    setError(null);
    setRecordingStartTime(Date.now());

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      startAudioVisualization(stream);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
      setError("Microphone access denied");
      stopAudioVisualization();
    }
  }, [startAudioVisualization, stopAudioVisualization]);

  // Stop recording and process
  const stopRecording = useCallback(async () => {
    stopAudioVisualization();
    setRecordingStartTime(null);

    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") {
      setIsRecording(false);
      return;
    }

    setIsProcessing(true);

    return new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current!;

      recorder.onstop = async () => {
        // Stop the media stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });

        try {
          const { data, response } = await api.POST("/api/speech-to-text", {
            // openapi-fetch types expect string for binary, but we need FormData
            body: { audio: audioBlob as unknown as string },
            bodySerializer: (body) => {
              const fd = new FormData();
              fd.append("audio", body.audio as unknown as Blob, "recording.webm");
              fd.append("language", language);
              return fd;
            },
          });

          if (!response.ok) {
            let message = "STT failed";
            try {
              const errorData = await response.json();
              message = errorData.detail || errorData.error || message;
            } catch {
              // Response wasn't JSON (e.g. HTML error page)
            }
            throw new Error(message);
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result = data as any;

          if (result.text) {
            onResult(result.text);
          } else {
            setError("No speech detected. Please try again.");
          }
        } catch (err) {
          console.error("[STT] Error:", err);
          setError(err instanceof Error ? err.message : "Failed to transcribe");
        } finally {
          setIsProcessing(false);
          setIsRecording(false);
          resolve();
        }
      };

      recorder.stop();
    });
  }, [language, onResult, stopAudioVisualization]);

  // Toggle recording
  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isProcessing,
    error,
    recordingStartTime,
    volumeLevel,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
